import React, { ReactElement, useEffect, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { AuditLogEventName } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { IAuditLogItemResponse } from 'models/auditLog';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { formatDate, getDateFromISO } from 'utils/date';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useUsers } from 'pages/UsersPage/hooks';
import { EAuditLogEvents } from 'pages/AuditLogPage/helpers';
import { StyledDivider, StyledViewSectionTitle } from 'pages/ApiKeysPage/styles';
import { apiKeyScopeList, IApiKeyResponse } from 'models/apiKey';
import { format } from 'date-fns';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useDictionary } from 'models/dictionaryContext';

interface IApiKeyEventDetails {
  auditLog: IAuditLogItemResponse;
}

const renderScopeName = (scope: string, item: string) => {
  const key = apiKeyScopeList.find((row) => row.value === scope);
  if (key) {
    const itemName = key?.items.find((name) => name.value === `${scope}_${item}`);
    if (itemName) {
      return itemName.label;
    }
  }
  return false;
};

const ApiKeyEventDetails = ({ auditLog }: IApiKeyEventDetails) => {
  const { t } = useTranslations();
  const { loadDictionaries } = useUsers();
  const { phoneCode } = useDictionary();
  const { shortDateFormat, longDateFormat, getProperties } = useGlobalProperty();
  const [previousApiKey, setPreviousApiKey] = useState<IApiKeyResponse | null>();
  const [postApiKey, setPostApiKey] = useState<IApiKeyResponse | null>();
  const [previousKeyScope, setPreviousKeyScope] = useState({
    apikeyAncillaryAccessory: [],
    apikeyAncillaryAmmunition: [],
    apikeyAncillaryComponent: [],
    apikeyFirearm: [],
    apikeyLegalEntity: [],
    apikeyTransaction: [],
    apikeyWeaponLicence: [],
  });
  const [keyScope, setKeyScope] = useState({
    apikeyAncillaryAccessory: [],
    apikeyAncillaryAmmunition: [],
    apikeyAncillaryComponent: [],
    apikeyFirearm: [],
    apikeyLegalEntity: [],
    apikeyTransaction: [],
    apikeyWeaponLicence: [],
  });
  const navigate = useNavigate();

  const setApiKeyData = async (
    apiKeyObject: any,
    setState: React.Dispatch<React.SetStateAction<IApiKeyResponse | null | undefined>>,
    setKeyScopeState: React.Dispatch<React.SetStateAction<any>>,
  ) => {
    const apiKeyData: IApiKeyResponse = {
      apikeyAncillaryAccessory: apiKeyObject.apikeyAncillaryAccessory || {},
      apikeyAncillaryAmmunition: apiKeyObject.apikeyAncillaryAmmunition || {},
      apikeyAncillaryComponent: apiKeyObject.apikeyAncillaryComponent || {},
      apikeyFirearm: apiKeyObject.apikeyFirearm || {},
      apikeyLegalEntity: apiKeyObject.apikeyLegalEntity || {},
      apikeyTransaction: apiKeyObject.apikeyTransaction || {},
      apikeyWeaponLicence: apiKeyObject.apikeyWeaponLicence || {},
      contactEmail: apiKeyObject.contactEmail || '',
      description: apiKeyObject.description || '',
      expiryDate: apiKeyObject.expiryDate || '',
      firstName: apiKeyObject.firstName || '',
      key: apiKeyObject.key || '',
      lastName: apiKeyObject.lastName || '',
      lastUsedDate: apiKeyObject.lastUsedDate || '',
      phoneNumber: apiKeyObject.phoneNumber || '',
      phonePrefixDictionaryUniqueId: apiKeyObject.phonePrefixDictionaryUniqueId || '',
      phonePrefixValue: apiKeyObject.phonePrefixValue || [],
      statusDictionaryUniqueId: apiKeyObject.statusDictionaryUniqueId || '',
      type: apiKeyObject.type || '',
      username: apiKeyObject.username || '',
      uniqueId: '',
    };
    setState(apiKeyData);

    const scopeKeys = {
      apikeyAncillaryAccessory: [],
      apikeyAncillaryAmmunition: [],
      apikeyAncillaryComponent: [],
      apikeyFirearm: [],
      apikeyLegalEntity: [],
      apikeyTransaction: [],
      apikeyWeaponLicence: [],
    };

    Object.keys(scopeKeys).map((key) => {
      const apiKeyItem = apiKeyObject?.[key];

      if (apiKeyItem) {
        Object.keys(apiKeyItem).map((item) => {
          if (apiKeyItem[item] === true) {
            // @ts-expect-error: required by update
            scopeKeys[key].push(renderScopeName(key, item));
          }
          return false;
        });
      }
      return false;
    });

    setKeyScopeState(scopeKeys);
  };

  useEffect(() => {
    if (auditLog?.previousObjectValue)
      setApiKeyData(auditLog?.previousObjectValue, setPreviousApiKey, setPreviousKeyScope);

    if (auditLog?.postObjectValue)
      setApiKeyData(auditLog?.postObjectValue, setPostApiKey, setKeyScope);
  }, [auditLog]);

  useEffect(() => {
    getProperties();
    loadDictionaries();
  }, []);

  const renderExpiryDate = (apiKey: IApiKeyResponse) => {
    if (!apiKey) {
      return null;
    }

    const date = getDateFromISO(apiKey.expiryDate);
    if (date === '') {
      return (
        <Chips
          text={t('deleteApiKey.state.perpetual.label')}
          variant={EChipsVariants.success}
        />
      );
    }
    if (date > new Date()) {
      return format(date, shortDateFormat);
    }
    return (
      <Chips text={t('deleteApiKey.state.expired.label')} variant={EChipsVariants.error} />
    );
  };

  const renderContactPhone = (phonePrefix: string, phoneNumber: string) =>
    `00${phonePrefix} ${phoneNumber}`;

  const renderKeyScope = (customKeyScope: {
    apikeyAncillaryAccessory: never[];
    apikeyAncillaryAmmunition: never[];
    apikeyAncillaryComponent: never[];
    apikeyFirearm: never[];
    apikeyLegalEntity: never[];
    apikeyTransaction: never[];
    apikeyWeaponLicence: never[];
  }) => {
    const renderItem: ReactElement[] = [];

    Object.keys(customKeyScope).map((key) => {
      // @ts-expect-error: required by update
      const scope = customKeyScope?.[key];
      if (scope?.length) {
        const title = apiKeyScopeList.find((item) => item.value === key)?.label;

        renderItem.push(
          <>
            <Grid size={{ xs: 12 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '10px' }}>
                  {title ? t(title) : '-'}
                </Typography>
              </Stack>
            </Grid>
            {scope.map((item: string) => (
              <Grid size={{ xs: 12, md: 6, lg: 3 }} key={item} style={{ paddingTop: '0px' }}>
                <Typography variant="body2">{t(item)}</Typography>
              </Grid>
            ))}
          </>,
        );
      }
      return false;
    });

    return renderItem;
  };

  return (
    <>
      {previousApiKey &&
        (auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_API_KEY ||
          auditLog?.eventNameUniqueId === EAuditLogEvents.DELETE_API_KEY) && (
          <>
            <AuditLogEventName>
              <Typography variant="h5">
                {`${t('viewApiKey.header')} ${t('auditLogDetails.before.event.title')}`}
              </Typography>
            </AuditLogEventName>
            <DetailsTemplate
              title={previousApiKey?.key}
              pageType={EDetailsPageTypes.auditLogView}
              redirectToView={() =>
                navigate(
                  `${ERouteLinks.ViewApiKeyPage.replace(':id', previousApiKey?.uniqueId)}`,
                )
              }
              disableActions={!previousApiKey.uniqueId}
            >
              <Grid sx={{ rowGap: '18px' }}>
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('viewApiKey.key.label')}
                    </Typography>
                    <Typography variant="body2">
                      <Chips text={previousApiKey.type} variant={EChipsVariants.primary} />
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('viewApiKey.user.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(previousApiKey?.username)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('viewApiKey.expiryDate.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(renderExpiryDate(previousApiKey))}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('viewApiKey.lastUsed.label')}
                    </Typography>
                    <Typography variant="body2">
                      {previousApiKey?.lastUsedDate
                        ? formatDate(previousApiKey.lastUsedDate, longDateFormat)
                        : '-'}
                    </Typography>
                  </Stack>
                </Grid>

                {previousApiKey?.description && (
                  <Grid size={{ xs: 12 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('viewApiKey.description.label')}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(previousApiKey?.description)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
              </Grid>

              <StyledDivider />

              <StyledViewSectionTitle variant="subtitle1">
                {t('viewApiKey.administrator.title')}
              </StyledViewSectionTitle>

              <Grid sx={{ rowGap: '18px' }}>
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('viewApiKey.contactEmail.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(previousApiKey?.contactEmail)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('viewApiKey.contactName.label')}
                    </Typography>
                    <Typography variant="body2">{`${previousApiKey?.firstName} ${previousApiKey.lastName}`}</Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('viewApiKey.contactPhone.label')}
                    </Typography>
                    {phoneCode && phoneCode.length > 0 && (
                      <Typography variant="body2">
                        {renderContactPhone(
                          phoneCode?.find(
                            (it) =>
                              it.uniqueId === previousApiKey.phonePrefixDictionaryUniqueId,
                          )?.name || '',
                          previousApiKey.phoneNumber,
                        )}
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>

              <StyledDivider />

              <StyledViewSectionTitle variant="subtitle1">
                {t('viewApiKey.scope.title')}
              </StyledViewSectionTitle>

              {renderKeyScope(previousKeyScope)}
            </DetailsTemplate>
          </>
        )}

      {auditLog && auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_API_KEY && (
        <AuditLogEventName sx={{ marginTop: '20px' }}>
          <Typography variant="h5">
            {`${t('viewApiKey.header')} ${t('auditLogDetails.post.event.title')}`}
          </Typography>
        </AuditLogEventName>
      )}

      {postApiKey && (
        <DetailsTemplate
          title={postApiKey?.key}
          pageType={EDetailsPageTypes.auditLogView}
          redirectToView={() =>
            navigate(`${ERouteLinks.ViewApiKeyPage.replace(':id', postApiKey?.uniqueId)}`)
          }
          disableActions={!postApiKey?.uniqueId}
        >
          <Grid sx={{ rowGap: '18px' }}>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('viewApiKey.key.label')}
                </Typography>
                <Typography variant="body2">
                  <Chips text={postApiKey.type} variant={EChipsVariants.primary} />
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('viewApiKey.user.label')}
                </Typography>
                <Typography variant="body2">
                  {emptyFieldHolder(postApiKey?.username)}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('viewApiKey.expiryDate.label')}
                </Typography>
                <Typography variant="body2">{renderExpiryDate(postApiKey)}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('viewApiKey.lastUsed.label')}
                </Typography>
                <Typography variant="body2">
                  {postApiKey?.lastUsedDate
                    ? formatDate(postApiKey.lastUsedDate, longDateFormat)
                    : '-'}
                </Typography>
              </Stack>
            </Grid>

            {postApiKey?.description && (
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('viewApiKey.description.label')}
                  </Typography>
                  <Typography variant="body2">
                    {emptyFieldHolder(postApiKey?.description)}
                  </Typography>
                </Stack>
              </Grid>
            )}
          </Grid>

          <StyledDivider />

          <StyledViewSectionTitle variant="subtitle1">
            {t('viewApiKey.administrator.title')}
          </StyledViewSectionTitle>

          <Grid sx={{ rowGap: '18px' }}>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('viewApiKey.contactEmail.label')}
                </Typography>
                <Typography variant="body2">
                  {emptyFieldHolder(postApiKey?.contactEmail)}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('viewApiKey.contactName.label')}
                </Typography>
                <Typography variant="body2">{`${postApiKey?.firstName} ${postApiKey.lastName}`}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('viewApiKey.contactPhone.label')}
                </Typography>
                <Typography variant="body2">
                  {renderContactPhone(
                    postApiKey?.phonePrefixValue?.[0]?.name,
                    postApiKey?.phoneNumber,
                  )}
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          <StyledDivider />

          <StyledViewSectionTitle variant="subtitle1">
            {t('viewApiKey.scope.title')}
          </StyledViewSectionTitle>

          {renderKeyScope(keyScope)}
        </DetailsTemplate>
      )}
    </>
  );
};

export default ApiKeyEventDetails;
